import Container from '@services/Container';
// eslint-disable-next-line no-unused-vars
import FormContextComplexFieldInterface from '@form/formcontext/FormContextComplexFieldInterface';
// eslint-disable-next-line no-unused-vars
import FormContext from '@desktop/components/FormContext';
const container = Container.getInstance();
const flashMessenger = container.getFlashMessenger();

// Mixin for managing form data using FormContext objects.
// Depends on this.formContexts
export default {
  props: {
    // Either a single or multiple FormContext objects can be passed.
    formContext: {
      type: Object,
      default: null,
    },
    formContexts: {
      type: Object,
      default: () => ({}),
    },
  },
  provide() {
    return {
      // This is the current solution to send events through <slot> boundaries.
      emitFieldChange: (...args) => this.handleFormFieldChange(...args),
    }
  },
  computed: {
    activeContexts() {
      if (this.initialFormContexts) {
        return Object.values(this.initialFormContexts);
      }
      if (Object.keys(this.formContexts).length) {
        return Object.values(this.formContexts);
      }
      if (this.formContext) {
        return [this.formContext];
      }
      return [];
    },
    editModeList() {
      return this.$route.query.editMode ?
        this.$route.query.editMode.split(',') : []
    },
  },
  watch: {
    '$route.query.editMode': {
      immediate: true,
      handler(val) {
        const actives = val ? val.split(',') : []
        this.activeContexts.forEach(ctx => {
          if (ctx.handleEditModeByQuery) {
            ctx.isInEditMode = actives.includes(ctx.name)
          }
          if (ctx.isAlwaysInEditMode) {
            ctx.isInEditMode = true;
          }
        })
      },
    },
  },
  data() {
    return {
      reloadHandlers: {},
    }
  },
  methods: {
    handleFormFieldChange(formContext, property) {
      if (
        Array.isArray(formContext.reloadFields) &&
        formContext.reloadFields.includes(property)
      ) {
        const handler = this.reloadHandlers[formContext.name];
        if (typeof handler === 'function') {
          return handler(formContext, property);
        }
      }
    },
    async reloadFormData(formContext, reloadData) {
      if (!formContext) {
        return null
      }
      try {
        const payload = formContext.form.getData();
        delete payload.isEntityNew; // isEntityNew is not a valid field, so we remove it

        // remove non-editable fields from payload
        const contextData = formContext.form.getContextData();
        this.filterByCanBeEdited(payload, contextData);

        const subformIds = formContext.form.subFormIdentifiers ?? [];

        subformIds.forEach((subformId) => {
            delete payload[subformId].data.attributes.isEntityNew;
            const subFormContextData = contextData[subformId];
            this.filterByCanBeEdited(payload[subformId].data.attributes, subFormContextData);
        });

        formContext.isLoading++;
        let resp;
        try {
          resp = await reloadData(formContext.objectId, payload)
        } finally {
          formContext.isLoading--;
        }

        this.applyServerData(resp.data, true)
        return resp
      } catch (err) {
        console.error('reloadFormData:', err);
        if (!formContext.form.setValidationErrors?.(err)) {
            // only report unknown error if caught error is not validation error
            flashMessenger.addError('Unbekannter Fehler beim Aktualisieren')
        }
      }
    },
    flattenAttributes(attrs) {
      // Extract the simple and complex fields from data.attributes
      const {
        fields = {}, isEntityNew,
      } = attrs;

      // Merge the simpleFields and the complexFields into a single object while
      // only using the "value" property of complexFields.
      return {
        ...Object.entries(fields).reduce((result, [key, value]) => {
          let fieldValue = value?.value ?? null;

          result[key] = fieldValue
          return result;
        }, {}),
        isEntityNew,
      };
    },
    // if isReload is true, we assume that the initial data from the form and the formContext data remain the same
    applyServerData(serverData, isReload = false) {
      const resource = serverData.data && typeof serverData.data === 'object' ?
        serverData.data :
        serverData;

      const attrs = resource.attributes;
      if (!attrs || typeof attrs !== 'object') {
        console.error('applyServerData: no attributes found on resource', resource);
        return;
      }

      this.activeContexts.forEach(formContext => {
        let formContextAttrs = null;
        let objectId = null;
        let formContextResource = null;
        if (Object.prototype.hasOwnProperty.call(attrs, formContext.name)) {
          // If transformer returns multiple contexts, we need to find the right one from attributes
          formContextAttrs = attrs[formContext.name]?.data?.attributes;
          objectId = attrs[formContext.name]?.data?.id;
          formContextResource = attrs[formContext.name]?.data;
        } else if (resource.type === formContext.name) {
          // If transformer returns a single context (e.g. default for reload),
          // we can use the resource directly
          objectId = resource.id;
          formContextAttrs = attrs;
          formContextResource = resource;
        }
        if (!formContextAttrs) {
          console.warn(`applyServerData: no data for context '${formContext.name}'`);
          return;
        }

        let flattened = this.flattenAttributes(formContextAttrs);
        let {fields} = formContextAttrs;

        // reset identifiers of subforms before processing subform data from backend
        // (especially relevant for reload or after save)
        let subformIdentifiers = [];

          // if we have subform data given, we need to flatten this as well
        for (const [index, item] of Object.entries(formContextAttrs.subformData)) {
          const subformIdentifier = index;
          const itemFlattened = this.flattenAttributes(item);
          const itemFields = item.fields;

          // add [data][attributes] before the actual data, because the form expects this structure for subforms
          // (as the backend transformer generate it for resources)
          flattened[subformIdentifier] = {
            data: {
              attributes: itemFlattened,
            },
          };
          fields[subformIdentifier] = itemFields;
          // Add the subform identifier to the formContext, so we can e.g. iterate over them in the template
          subformIdentifiers.push(subformIdentifier);
        }

        if (Object.prototype.hasOwnProperty.call(formContextResource, 'meta')
          && Object.prototype.hasOwnProperty.call(formContextResource.meta, 'permissions')) {
          this.$set(formContext, 'permissions', formContextResource.meta.permissions);
        }
        formContext.isLoaded = true;
        formContext.isEditable = true;

        // we always set the subform identifiers in the form
        formContext.form.setSubformIdentifiers(JSON.parse(JSON.stringify(subformIdentifiers)));

        // allof components to render updated subform identifiers before changing the form data
        this.$nextTick(() => {
          // explicitly use copy of flattened, so that we keep formContext data separate from the form data
          const flattenedCopy = JSON.parse(JSON.stringify(flattened));
          // TODO Whenever we want to create new subforms with a complexType, we need to add another complexFields entry
          // that holds the complexFields for the data state of the form.
          if (!isReload) {
            // The formContext.data and complexFields are only updated if the data was stored in database already!
            // non persistent changes of the data are only hold in the formContext.form.data
            this.$set(formContext, 'data', flattened);
            this.$set(formContext, 'complexFields', fields);
            this.$set(formContext, 'objectId', objectId);
            formContext.form.setData(flattenedCopy);
            formContext.form.setContextData(fields);
            // we only set the subform identifiers in the form context when it is not a reload
            formContext.subformIdentifiers = JSON.parse(JSON.stringify(subformIdentifiers));
          } else {
            // if isReload is true, we use setDataWithoutInitial to keep initial data in the form unchanged
            this.$set(formContext, 'data', flattened);
            formContext.form.setDataWithoutInitial(flattenedCopy);
            formContext.form.setContextDataWithoutInital(fields);
          }
        })
      });
    },
    /**
     * this method allows to add a new subform to the given form context from the frontend directly,
     * instead of using backend transformer to create a blueprint structure that the frontend parses in applyServerData
     *
     * @param subformIdentifier {string}
     * @param formContextFields {Record<string, FormContextComplexFieldInterface>}}
     * @param formContext {FormContext}
     */
    addSubformToFormContext(subformIdentifier, formContextFields, formContext) {
      const attributes = {
        'fields': formContextFields,
        'isEntityNew': true,
      };

      const flattenedAttributes = this.flattenAttributes(attributes);

      // add [data][attributes] before the actual data, because the form expects this structure for subforms
      // (as the backend transformer generate it for resources)
      let flattened = {};
      flattened[subformIdentifier] = {
        data: {
          attributes: flattenedAttributes,
        },
      };

      let fields = {};
      fields[subformIdentifier] = formContextFields;

      this.$set(formContext, 'data', Object.assign(formContext.data, flattened));
      this.$set(formContext, 'complexFields', Object.assign(formContext.complexFields, fields));

      // merge contextData with new subform context data
      const existingContextData = formContext.form.getContextData();
      formContext.form.contextData = Object.assign(existingContextData, fields)

      // merge contextDataInitial with new subform context data
      const existingContextDataInitial = JSON.parse(formContext.form.contextDataInitial);
      formContext.form.contextDataInitial = JSON.stringify(Object.assign(existingContextDataInitial, fields));

      // explicitly use copy of flattened, so that we keep formContext data separate from the form data
      const flattenedAttributesCopy = JSON.parse(JSON.stringify(flattenedAttributes));

      formContext.form.createSubformWithData(flattenedAttributesCopy, subformIdentifier);

      this.$nextTick(() => {
        formContext.subformIdentifiers = JSON.parse(JSON.stringify(formContext.form.getSubformIdentifiers()));
      });
    },

    filterByCanBeEdited(data, contextData) {
          Object.keys(data).forEach((key) => {
              const fieldContextData = contextData?.[key];

              // if the context data of a field says non-editable, remove the field
              if (fieldContextData?.canBeEdited === false) {
                  delete data[key];
              }
          });
      },

    /**
     * @param returnRoute route that is used when exiting edit mode; prevents exit when value is false
     */
    async saveFormData(formContext, saveData, returnRoute = null) {
      if (!formContext?.isInEditMode) return null;
      formContext.form.clearValidationErrors();
      const payload = formContext.form.getData();
      delete payload.isEntityNew; // isEntityNew is not a valid field, so we remove it

      // remove non-editable fields from payload
      const contextData = formContext.form.getContextData();
      this.filterByCanBeEdited(payload, contextData);

      const subformIds = formContext.form.subFormIdentifiers ?? [];

      subformIds.forEach((subformId) => {
          delete payload[subformId].data.attributes.isEntityNew;
          const subFormContextData = contextData[subformId];
          this.filterByCanBeEdited(payload[subformId].data.attributes, subFormContextData);
      });

      formContext.isLoading++;
      const result = saveData(formContext.objectId, payload)
        .then((response) => {
          if (response.data.type === 'success') {
              // ATTENTION: this does not update complexFields!
              // If the save request does not return the updated resource, but only a success type,
              // we still update the state with the information we have in the frontend.
              // If no complex field are changed, this is sufficient.

              // override initial data with the saved data
              formContext.form.setData(JSON.parse(JSON.stringify(formContext.form.getData())));
              // explicitly use copy of data, so that we keep formContext data separate from the form data
              this.$set(formContext, 'data', JSON.parse(JSON.stringify(formContext.form.getData())));
              // after save, the entity can never be new
              formContext.data.isEntityNew = false;
          } else if (response.data.type === 'redirect') {
              // navigate to the URL that is specified by the redirect response
              flashMessenger.addSuccess('Speichern erfolgreich');
              window.location.href = response.data.attributes.url;
              return response;
          } else {
              // if the save request returns the updated resource, we apply it from backend
              this.applyServerData(response.data, false);
          }

          flashMessenger.addSuccess('Speichern erfolgreich')
          if (returnRoute !== false) {
              this.exitEditMode(returnRoute);
          }
          // return response object to allow chaining of further 'then'-calls
          return response;
        })
        .catch(err => {
          formContext.form.setValidationErrors(err);
          throw err;
        })
        .finally(() => {
          formContext.isLoading--;
        });

      return result;
    },
    async loadFormData(loadData) {
      try {
        const data = await loadData();
        this.applyServerData(data);
        return data;
      } catch (err) {
        console.error('loadFormData:', err);
        flashMessenger.addError('Laden fehlgeschlagen');
      }
    },
    exitEditMode(returnRoute = null) {
      const query = {
        ...this.$route.query,
      }
      delete query.editMode
      this.$router.push(
        returnRoute ? {
          name: returnRoute,
        } : {
          name: this.$route.name,
          query,
        }
      )
    },
  },
}
